import { loadApiHealthCheck } from '@/services';
import { quickLog } from '@/utils';
import { FEATURE_FLAGS, ROUTES } from '@/constants';
import { useAuth } from '@/components/Auth';
import { useEffect } from 'react';
import { useGlobalStore } from '../global-store';
import { usePathname } from 'next/navigation';

/**
 * Initializes API health status in the global store.
 *
 * This hook runs on app route changes to set the API health status. It also gathers
 * basic environment and user context information to be used in API calls and for
 * feature flag evaluation.
 */
export const useApiHealthCheck = (): void => {
  const { logout } = useAuth();
  const { setApiHealth, setShowOutageAlert } = useGlobalStore();
  const pathName = usePathname();
  const localStorageKey = 'apiHealthCheck';

  useEffect(() => {
    const persistHealthStatus = (status: string) => {
      // During test teardown, localStorage may be unavailable.
      if (typeof globalThis.localStorage === 'undefined') return;

      try {
        globalThis.localStorage.setItem(localStorageKey, status);
      } catch {
        // Ignore storage write failures; health state is still stored in Zustand.
      }
    };

    const runHealthCheck = async () => {
      // Eject if health checks are disabled via feature flag.
      if (FEATURE_FLAGS.ENABLE_HEALTH_CHECKS === false) return;

      // Ensure this only runs client side
      if (typeof window === 'undefined') return;

      // Try/catch
      try {
        // Load API health status from services layer.
        const check = await loadApiHealthCheck();

        // Update global store with API health status.
        setApiHealth(check);

        // Persist API health status in localStorage for login view screen.
        persistHealthStatus(check.status);

        // Log the health check result for debugging purposes.
        quickLog.api('API Health Check:', check);

        // Set showOutageAlert based on health status.
        if (check.status !== 'healthy') {
          setShowOutageAlert(true);
          if (pathName !== ROUTES.LOGIN) logout();
          persistHealthStatus(check.status);
        }
      } catch (error) {
        // On error, log and set API health to false.
        console.error('API health check failed:', error);
        const status = error instanceof Error ? error.message : 'unreachable';
        setApiHealth({ environment: 'unknown', project: 'unknown', status });
        setShowOutageAlert(true);
        if (pathName !== ROUTES.LOGIN) logout();
        persistHealthStatus(status);
      }
    };

    runHealthCheck();
  }, [logout, pathName, setApiHealth, setShowOutageAlert]);
};
